/**
 * Precision Planning — Chemical Compliance  (mvp2_agrotech_precision)
 *
 * Provides a cross-field view of all chemical applications, verifying that:
 *   1. Total season dosage per chemical stays within configured maximums
 *   2. No-spray buffer zones are respected
 *   3. Pre-harvest intervals (PHI) are met for scheduled treatments
 *
 * Data sources (all existing endpoints):
 *  • GET /api/missions?mission_type=remediation — all remediation missions with spray metadata
 *  • GET /api/satellite/boundaries              — field area for density calculations
 *
 * Compliance thresholds are stored client-side in default tables below.
 * Organisation Admins can override them in the backend; the UI reads the
 * worst-case outcome from the spray metadata already recorded.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldAlert, RefreshCw, CheckCircle2, AlertTriangle,
  XCircle, ChevronDown, ChevronUp, Download, Info,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { getAllMissions, invalidateCache } from '../../services/precisionDataCache';
import { useOrgSettings } from '../../hooks/useOrgSettings';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface SpraySpec {
  chemical?: string;
  rate_g_per_l?: number;
  rate_kg_per_ha?: number;
  total_volume_l?: number;
  area_ha?: number;
  buffer_m?: number;
}

interface RemediationMission {
  id: string;
  name: string;
  status: string;
  mission_type: string;
  created_at: string;
  metadata?: {
    remediation_type?: string;
    target_zone_ha?: number;
    spray_1?: SpraySpec;
    spray_2?: SpraySpec;
    boundary_id?: string;
    anomaly_id?: string;
    [key: string]: any;
  };
}

interface ChemicalRecord {
  chemical: string;
  totalVolumeL: number;
  totalAreaHa: number;
  avgRateKgPerHa: number;
  missionIds: string[];
  applications: {
    missionId: string;
    missionName: string;
    date: string;
    areaHa: number;
    volumeL: number;
    rateKgPerHa: number;
    buffer_m?: number;
  }[];
}

interface ComplianceIssue {
  chemical: string;
  type: 'max_dosage_exceeded' | 'no_buffer' | 'phi_risk';
  message: string;
  severity: 'warning' | 'critical';
}

// ─── Compliance thresholds (defaults — configurable per org) ──────────────────

const MAX_SEASON_KG_PER_HA: Record<string, number> = {
  'Copper fungicide':     20,
  'Glyphosate':           5,
  'Carbendazim':          2,
  'Lambda-cyhalothrin':   0.5,
  'Imidacloprid':         0.3,
  'Chlorpyrifos':         1.0,
  'Mancozeb':             15,
  'Propiconazole':        1.5,
  'Dithane':              12,
  'Urea (46-0-0)':        300,   // fertiliser — higher limit
  'NPK 17-17-17':         250,
  'DAP (18-46-0)':        200,
  'MOP (0-0-60)':         200,
  'NPK 20-20-0':          250,
};

const MIN_BUFFER_REQUIRED_M = 10;   // minimum spray buffer near water bodies

const PHI_DAYS: Record<string, number> = {
  'Carbendazim':          14,
  'Lambda-cyhalothrin':   7,
  'Imidacloprid':         28,
  'Chlorpyrifos':         14,
  'Propiconazole':        14,
  'Glyphosate':           7,
};

// ─── Compute compliance from chemical record ──────────────────────────────────

function assessChemicalCompliance(rec: ChemicalRecord): ComplianceIssue[] {
  const issues: ComplianceIssue[] = [];
  const maxRate = MAX_SEASON_KG_PER_HA[rec.chemical];

  if (maxRate !== undefined && rec.avgRateKgPerHa > maxRate) {
    issues.push({
      chemical: rec.chemical,
      type: 'max_dosage_exceeded',
      message: `Season average rate ${rec.avgRateKgPerHa.toFixed(2)} kg/ha exceeds configured maximum ${maxRate} kg/ha`,
      severity: 'critical',
    });
  }

  const missingBuffer = rec.applications.some(a => a.buffer_m !== undefined && a.buffer_m < MIN_BUFFER_REQUIRED_M);
  if (missingBuffer) {
    issues.push({
      chemical: rec.chemical,
      type: 'no_buffer',
      message: `One or more applications have a no-spray buffer < ${MIN_BUFFER_REQUIRED_M} m from waterways`,
      severity: 'warning',
    });
  }

  const phi = PHI_DAYS[rec.chemical];
  if (phi !== undefined && rec.applications.length > 0) {
    const lastApp = rec.applications[rec.applications.length - 1];
    const daysSince = Math.floor((Date.now() - new Date(lastApp.date).getTime()) / 86_400_000);
    if (daysSince < phi) {
      issues.push({
        chemical: rec.chemical,
        type: 'phi_risk',
        message: `Last application ${daysSince} day(s) ago — PHI for ${rec.chemical} is ${phi} days`,
        severity: 'warning',
      });
    }
  }

  return issues;
}

// ─── CSV export ───────────────────────────────────────────────────────────────

function buildComplianceCsv(records: ChemicalRecord[]): string {
  const lines = [
    '# Nabhos Chemical Compliance Report',
    `# Generated: ${new Date().toISOString()}`,
    '',
    'Chemical,Total_Applications,Total_Area_ha,Total_Volume_L,Avg_Rate_kg_per_ha,Max_Allowed_kg_per_ha,Status',
    ...records.map(r => {
      const maxRate = MAX_SEASON_KG_PER_HA[r.chemical] ?? 'n/a';
      const issues = assessChemicalCompliance(r);
      const status = issues.some(i => i.severity === 'critical') ? 'BREACH' : issues.length > 0 ? 'WARNING' : 'COMPLIANT';
      return `"${r.chemical}",${r.applications.length},${r.totalAreaHa.toFixed(2)},${r.totalVolumeL.toFixed(1)},${r.avgRateKgPerHa.toFixed(3)},${maxRate},${status}`;
    }),
  ];
  return lines.join('\n');
}

function downloadText(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function ComplianceBadge({ issues }: { issues: ComplianceIssue[] }) {
  if (issues.length === 0) return (
    <span className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
      <CheckCircle2 className="w-3 h-3" />COMPLIANT
    </span>
  );
  const hasCritical = issues.some(i => i.severity === 'critical');
  return (
    <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded ${hasCritical ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'}`}>
      {hasCritical ? <XCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
      {hasCritical ? 'BREACH' : 'WARNING'}
    </span>
  );
}

function ChemicalCard({ rec }: { rec: ChemicalRecord }) {
  const [expanded, setExpanded] = useState(false);
  const { uom } = useOrgSettings();
  const issues = assessChemicalCompliance(rec);
  const maxRate = MAX_SEASON_KG_PER_HA[rec.chemical];

  return (
    <Card>
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <p className="text-base font-bold text-gray-900 dark:text-white">{rec.chemical}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {rec.applications.length} application{rec.applications.length !== 1 ? 's' : ''} · {rec.totalAreaHa.toFixed(1)} {uom.area_unit_label} treated
            </p>
          </div>
          <ComplianceBadge issues={issues} />
        </div>

        {/* Rate comparison bar */}
        {maxRate !== undefined && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-[10px] text-gray-400 mb-1">
              <span>Season avg rate</span>
              <span>{rec.avgRateKgPerHa.toFixed(2)} / {maxRate} kg/ha limit</span>
            </div>
            <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className={`h-2 rounded-full transition-all ${rec.avgRateKgPerHa > maxRate ? 'bg-red-500' : rec.avgRateKgPerHa > maxRate * 0.8 ? 'bg-amber-500' : 'bg-green-500'}`}
                style={{ width: `${Math.min(100, (rec.avgRateKgPerHa / maxRate) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Issues */}
        {issues.map((issue, i) => (
          <div key={i} className={`flex items-start gap-2 rounded-lg p-2 mb-2 text-xs leading-relaxed ${issue.severity === 'critical' ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300' : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'}`}>
            {issue.severity === 'critical' ? <XCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> : <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />}
            {issue.message}
          </div>
        ))}

        {/* Expand: application log */}
        <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
          <button type="button" onClick={() => setExpanded(v => !v)}
            className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex items-center gap-1">
            {expanded ? <><ChevronUp className="w-3.5 h-3.5" />Hide applications</> : <><ChevronDown className="w-3.5 h-3.5" />Application log</>}
          </button>
          {expanded && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700">
                    <th className="pb-1 pr-3 text-gray-400 font-medium">Mission</th>
                    <th className="pb-1 pr-3 text-gray-400 font-medium">Date</th>
                    <th className="pb-1 pr-3 text-gray-400 font-medium">Area ({uom.area_unit_label})</th>
                    <th className="pb-1 pr-3 text-gray-400 font-medium">Volume (L)</th>
                    <th className="pb-1 pr-3 text-gray-400 font-medium">Rate (kg/ha)</th>
                    <th className="pb-1 text-gray-400 font-medium">Buffer (m)</th>
                  </tr>
                </thead>
                <tbody>
                  {rec.applications.map(a => (
                    <tr key={a.missionId} className="border-b border-gray-50 dark:border-gray-800">
                      <td className="py-1 pr-3 text-gray-700 dark:text-gray-300 max-w-[120px] truncate">{a.missionName}</td>
                      <td className="py-1 pr-3 text-gray-500">{a.date.slice(0, 10)}</td>
                      <td className="py-1 pr-3 text-gray-700 dark:text-gray-300">{a.areaHa.toFixed(2)}</td>
                      <td className="py-1 pr-3 text-gray-700 dark:text-gray-300">{a.volumeL.toFixed(1)}</td>
                      <td className="py-1 pr-3 text-gray-700 dark:text-gray-300">{a.rateKgPerHa.toFixed(3)}</td>
                      <td className={`py-1 ${a.buffer_m !== undefined && a.buffer_m < MIN_BUFFER_REQUIRED_M ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-500'}`}>
                        {a.buffer_m ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// ─── Aggregate missions → chemical records ────────────────────────────────────

function aggregateChemicals(missions: RemediationMission[]): ChemicalRecord[] {
  const map: Record<string, ChemicalRecord> = {};

  for (const m of missions) {
    if (m.mission_type !== 'remediation') continue;
    const meta = m.metadata;
    if (!meta) continue;

    const sprays: SpraySpec[] = [meta.spray_1, meta.spray_2].filter(Boolean) as SpraySpec[];
    for (const spray of sprays) {
      if (!spray.chemical) continue;
      const key = spray.chemical;
      if (!map[key]) {
        map[key] = {
          chemical: key,
          totalVolumeL: 0,
          totalAreaHa: 0,
          avgRateKgPerHa: 0,
          missionIds: [],
          applications: [],
        };
      }
      const rec = map[key];
      const areaHa  = spray.area_ha ?? meta.target_zone_ha ?? 0;
      const volumeL = spray.total_volume_l ?? 0;
      // rate: prefer explicit rate, else derive from volume + area
      const rateKgPerHa =
        spray.rate_kg_per_ha ??
        (areaHa > 0 ? volumeL / areaHa / 10 : 0);

      rec.totalVolumeL += volumeL;
      rec.totalAreaHa  += areaHa;
      rec.missionIds.push(m.id);
      rec.applications.push({
        missionId: m.id,
        missionName: m.name,
        date: m.created_at,
        areaHa,
        volumeL,
        rateKgPerHa: +rateKgPerHa.toFixed(4),
        buffer_m: spray.buffer_m,
      });
    }
  }

  // Compute avg rate
  for (const rec of Object.values(map)) {
    if (rec.totalAreaHa > 0) {
      const totalKg = rec.applications.reduce((s, a) => s + a.rateKgPerHa * a.areaHa, 0);
      rec.avgRateKgPerHa = +(totalKg / rec.totalAreaHa).toFixed(4);
    }
  }

  // Sort: breaches first, then warnings, then compliant
  return Object.values(map).sort((a, b) => {
    const ia = assessChemicalCompliance(a);
    const ib = assessChemicalCompliance(b);
    const pa = ia.some(i => i.severity === 'critical') ? 0 : ia.length > 0 ? 1 : 2;
    const pb = ib.some(i => i.severity === 'critical') ? 0 : ib.length > 0 ? 1 : 2;
    return pa - pb;
  });
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export const ChemicalCompliancePage: React.FC = () => {
  const [records, setRecords]     = useState<ChemicalRecord[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);

  const load = useCallback(async (bust = false) => {
    try {
      setLoading(true);
      setError(null);
      if (bust) invalidateCache();

      const missions = await getAllMissions() as RemediationMission[];
      setRecords(aggregateChemicals(missions));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load mission data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const breachCount   = records.filter(r => assessChemicalCompliance(r).some(i => i.severity === 'critical')).length;
  const warningCount  = records.filter(r => {
    const is = assessChemicalCompliance(r);
    return is.length > 0 && !is.some(i => i.severity === 'critical');
  }).length;
  const compliantCount = records.length - breachCount - warningCount;

  const handleExport = () =>
    downloadText(buildComplianceCsv(records), `chemical_compliance_${new Date().toISOString().slice(0, 10)}.csv`);

  return (
    <div className="space-y-6">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ShieldAlert className="w-7 h-7 text-amber-600" /> Chemical Compliance
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Season dosage ledger, no-spray buffer audit, and pre-harvest interval (PHI) checks across all remediation missions
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={handleExport} disabled={loading || records.length === 0}>
            <Download className="w-3.5 h-3.5 mr-1.5" />Export CSV
          </Button>
          <button type="button" onClick={() => load(true)} disabled={loading}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Summary ────────────────────────────────────────────────────────── */}
      {!loading && records.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Chemicals Tracked', value: records.length, colour: 'text-gray-700 dark:text-gray-300' },
            { label: 'Compliant',         value: compliantCount, colour: 'text-green-600' },
            { label: 'Warnings',          value: warningCount,   colour: 'text-amber-600' },
            { label: 'Breaches',          value: breachCount,    colour: breachCount > 0 ? 'text-red-600' : 'text-gray-400' },
          ].map(({ label, value, colour }) => (
            <Card key={label} className="p-4 text-center">
              <p className={`text-2xl font-bold ${colour}`}>{value}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
            </Card>
          ))}
        </div>
      )}

      {/* ── Policy note ────────────────────────────────────────────────────── */}
      <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 p-4 flex gap-3">
        <Info className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
          <strong>Compliance guardrails:</strong> Maximum season dosage thresholds and PHI values shown are
          platform defaults. Organisation Admins can override per-chemical limits in Settings → Approved Assets.
          A <span className="font-semibold text-red-600 dark:text-red-400">BREACH</span> status means the
          configured limit has been exceeded and requires immediate agronomist review.
          A <span className="font-semibold text-amber-600 dark:text-amber-400">WARNING</span> indicates
          a buffer or PHI issue that needs attention before the next application.
        </div>
      </div>

      {/* ── Content ────────────────────────────────────────────────────────── */}
      {error && <Alert type="error">{error}</Alert>}

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : records.length === 0 ? (
        <Card className="p-10 text-center">
          <ShieldAlert className="mx-auto w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No chemical applications recorded</p>
          <p className="text-xs text-gray-400 mt-1">
            Chemical compliance data is derived from completed Remediation Missions (Step 8).
            Once precision spray missions are recorded, dosage tracking appears here.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {records.map(rec => <ChemicalCard key={rec.chemical} rec={rec} />)}
        </div>
      )}
    </div>
  );
};

export default ChemicalCompliancePage;
// Made with Bob
